<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="450">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="record">Update "{{ record.name }}" Payment Type</h4>
                    <h4 v-else>New Payment Type</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <template v-if="user.type === 'admin'">
                                <company-select v-model="formData['company_id']"
                                          :items="companies"
                                          density="compact" label="Company"
                                          @update:model-value="()=>{formData['driver_id']=null;fetchDriversList()}">
                                </company-select>
                            </template>
                            <v-select v-model="formData['driver_id']"
                                      :items="drivers"
                                      clearable
                                      density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id" label="Driver">
                            </v-select>

                            <v-text-field v-model="formData['name']"
                                          :rules="[vRules.required,vRules.maxLength(50)]"
                                          autofocus density="compact" label="Name"/>
                            <v-text-field v-model="formData['last5']"
                                          :rules="[vRules.required,vRules.exactLength(5)]"
                                          density="compact" label="Last 5 characters" maxlength="5"/>
                            <v-textarea v-model="formData['description']" density="compact" label="Description"
                                        maxlength="255"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CompanySelect from "@/components/globals/company-select.vue";

const snackbar = useSnackbarStore();
const {user} = useAuthStore();
const props = defineProps({
    modelValue: Boolean,
    record: Object,
});
const formData = reactive({
    valid: false,
    name: props.record?.name ?? null,
    last5: props.record?.last5 ?? null,
    driver_id: props.record?.driver_id ?? null,
    description: props.record?.description ?? null,
    company_id: props.record ? props.record?.company_id : ((user.type !== 'admin') ? user.company.id : null),
});
const emits = defineEmits(['update:modelValue', 'addRecord', 'updateRecord']);
const dialogSwitch = ref(props.modelValue);
const companies = ref([]);
const drivers = ref([]);
const errors = ref([]);

onMounted(() => {
    if (user.type === 'admin') {
        fetchCompaniesList()
    }
    fetchDriversList();
})

const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchDriversList = () => {
    send('get', `companies/drivers?list=true`, {company: formData['company_id']})
        .then((response) => {
            drivers.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Drivers List could not be loaded', 'error')
        })
}
const submitForm = () => {
    errors.value = [];
    send(`${props.record ? 'put' : 'post'}`,
        `driver-payment-types${props.record ? '/' + props.record.id : ''}`,
        formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits(props.record ? 'updateRecord' : 'addRecord', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response?.data?.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response?.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
